<template>
	<div class="xs-12">
		<j-card
			v-for="benefit in benefitClaims"
			:key="benefit.id"
			class="bg5 mb2 bb1 bc7"
		>
			<div class="xs-12 fw7 lh125 fs5 mb2 c6 pr4 pl4 pt4">
				{{ benefit.benefitName }}
			</div>

      <div class="mt4 bb1 bc7 pb6 pr4 pl4 pb4">
        <!-- Billed, Approved, Remaining summary per benefit -->
        <div class="xs-12 flex xs-bottom wrap ">
          <div class="xs-4">
            <div class="fs6 fw7 lh125">
              {{
                benefit.totalValues && benefit.totalValues.formattedBilledAmount
                  ? benefit.totalValues.formattedBilledAmount
                  : "-"
              }}
            </div>
            <div class="fs3 c6 fw7">{{ $t("billed_amount") }}</div>
          </div>
          <div class="xs-4">
            <div class="fs6 fw7 lh125">
              {{
                benefit.totalValues && benefit.totalValues.formattedApprovedAmount
                  ? benefit.totalValues.formattedApprovedAmount
                  : "-"
              }}
            </div>
            <div class="fs3 c6 fw7">{{ $t("approved_amount") }}</div>
          </div>
          <div class="xs-4">
            <div
              class="fs4 fw7 lh125"
              :class="{ cError: benefit.isValid === false }"
            >
              {{ benefit.remainingValue ? benefit.remainingValue : "-" }}
            </div>
            <div class="fs3 c6 fw7">{{ $t("remaining_amount") }}</div>
          </div>
        </div>

        <!-- Error Messages -->
        <div class="mt4" v-if="getErrorMessage(benefit)">
          <p class="fs3 fw6 cError mb1" v-for="(item,i) in getErrorMessage(benefit)" :key="i">
            {{item}}
          </p>
        </div>
      </div>

			<!-- Edit form -->
			<div v-if="canEditApprovedClaims(claimStatus)">

				<!-- Display proper template to collect values -->
        <j-accordion class="bb1 bc7" bodyClass="p3">
          <template v-slot:title>{{ $t("configure") }}</template>
          <template v-slot:body>
            <benefit-values-template
              :benefitClaimId="benefit.id"
              :benefitId="benefit.benefit ? benefit.benefit.typeId : null"
              :values="benefit.values"
              :benefit="benefit"
              :key="forcedRerender"
              @updated="updateBenefitClaim"
            />
          </template> 
        </j-accordion>

        <!-- Remark -->
        <j-accordion class="bb1 bc7" bodyClass="p3">
          <template v-slot:title>{{ $t("remark") }}</template>
          <template v-slot:body>
            <benefit-remark
              :id="benefit.id" 
              :payload="benefit.remark" 
              @update="updateRemark"
            />
          </template> 
        </j-accordion>
        
				<!-- Delete -->
				<div class="flex xs-12 mt4 xs-right pr4 pl4 pb4">
					<j-button
						size="xsmall"
						type="error"
						@click="removeBenefit(benefit.id)"
					>
						{{ $t("remove_benefit") }}
					</j-button>
				</div>
			</div>
		</j-card>

		<!-- Add button -->
		<j-card 
      v-if="canEditApprovedClaims(claimStatus)"
      class="flex xs-center bg5 p2 mb2 bb1 bc7">
			<j-button
				type="primary"
				size="small"
				@click="$modal.show('add_benefits')"
			>
				<crm-icon name="add" color="c5" size="small" />
				{{ $t("add_benefits") }}
			</j-button>
		</j-card>

		<!-- Add modal -->
		<core-modal name="add_benefits" :headline="$t('add')" width="800">
			<div class="p4 bg4">
				<add-benefits-to-claim
					:policyId="policyId"
					@input="benefitsToAddToClaim = $event"
				/>
				<div class="flex xs-right">
					<j-button @click="addBenefits" :isLoading="isLoading" class="mt6">
						{{ $t("add_benefits") }}
					</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JList from "@/components/JList"
import JCard from "@/components/JCard"
import CrmIcon from "@/components/CrmIcon"
import JButton from "@/components/JButton"
import JAccordion from "@/components/JAccordion/"
import CoreModal from "@/modules/core/components/CoreModal"
import AddBenefitsToClaim from "./AddBenefitsToClaim"
import UpdateClaimPartial from "./UpdateClaimPartial"
import BenefitValuesTemplate from "./BenefitValuesTemplates/index.vue"
import BenefitRemark from "./BenefitRemark.vue"
import dictionary from "./BenefitValuesTemplates/dictionary.js"
import { canEditApprovedClaims } from "../utils/"

import api from "../api/"

export default {
	components: {
		JList,
		JCard,
		CrmIcon,
    JButton,
    JAccordion,
		CoreModal,
		AddBenefitsToClaim,
		UpdateClaimPartial,
    BenefitValuesTemplate,
    BenefitRemark,
	},
	props: {
		benefitClaims: {
			type: Array,
			default: () => []
		},
		claimId: {
			type: String
		},
		policyId: {
			type: String
    },
    claimStatus: {
      type: String
    }
	},

	data() {
		return {
			isLoading: false,

			// List of benefits to add
			benefitsToAddToClaim: null,

			// Benefit update
			isUpdateBenefitLoading: false,

			forcedRerender: "1" // when update of benefits fail, we force rerender of benefit-values-template to update internal state of the form
		}
	},
	
	methods: {
		async removeBenefit(benefitClaimId) {
			const res = await api.removeBenefitFromClaim({
				claimId: this.claimId,
				benefitClaimId
			})

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("deleted")
		},

		async addBenefits() {
			if (!this.benefitsToAddToClaim) return

			this.isLoading = true
			const addBenefits = await Promise.all(
				this.benefitsToAddToClaim.map(item =>
					api.addBenefitToClaim({
						claimId: this.claimId,
						input: {
							benefitTypeId: item.typeId || null,
							currencyCode: item.currencyCode || null,
							benefitDescription: item.description,
							benefitName: item.name,
							value: item.claimedValue || null
						}
					})
				)
			)

			// Check errors
			if (
				addBenefits.some(
					item => item?.data?.addBenefitToClaim?.status !== "success"
				)
			) {
				this.$store.dispatch("showError", [this.$t("errors.general")])
				this.isLoading = false
				return
			}

			// Success
			this.isLoading = false
			this.benefitsToAddToClaim = null
			this.$modal.hide("add_benefits")
			this.$emit("added")
		},

		async updateBenefitClaim({ values, benefitClaimId }) {
			if (!values) return

			this.isUpdateBenefitLoading = true
			const res = await api.updateBenefitClaim({
				claimId: this.claimId,
				benefitClaimId,
				input: {
					values: values.map(item => ({
						approvedAmount: item.approvedAmount,
						billedAmount: item.billedAmount,
						approvedCurrency: item.approvedCurrency,
						billedCurrency: item.billedCurrency,
						unit: item.unit
					}))
				}
			})
			this.isUpdateBenefitLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.$emit("updated")
				this.forcedRerender = Math.random() // force rerender component to update its local state based on latest fecthed data
				return
			}

			// Success
			this.$emit("updated")
    },

    async updateRemark(payload) {
			 this.isUpdateBenefitLoading = true
			 const res = await api.updateBenefitClaim({
			 	claimId: this.claimId,
			 	benefitClaimId: payload.benefitClaimId,
			 	input: {
            remark: payload.remark
          }
        })
			 this.isUpdateBenefitLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.$emit("updated")
				this.forcedRerender = Math.random() // force rerender component to update its local state based on latest fecthed data
				return
      }
      
      this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("remark_updated")
				}
			})

			 // Success
			 this.$emit("updated")
    },

    
    getErrorMessage(payload){
      
      let errors = []

       let remainingValue = payload?.remainingScalarValue?.objectValue
          .filter(item => item.key === 'perPolicyYear')
          .map(item => item.value.numberValue)
     
      if (payload.totalValues.approvedAmount > payload.totalValues.billedAmount) 
        errors.push("Approved amount is greater than Billed amount") 

      // check if negative number  
      if (Math.sign(remainingValue?.[0]) === -1)
        errors.push("Approved amount is greater than Remaining amount") 

      if (errors.length > 0) return errors 
      else return null
     
      
    },

    canEditApprovedClaims: canEditApprovedClaims
  }
}
</script>